import { createTheme, alpha } from "@mui/material/styles";

const fontStack = "'K2D', 'Segoe UI', system-ui, sans-serif";

const typography = {
    fontFamily: fontStack,
    fontSize: 14,
    h4: { fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.2 },
    h5: { fontWeight: 700, letterSpacing: "-0.02em" },
    h6: { fontWeight: 600, letterSpacing: "-0.015em" },
    button: { fontWeight: 700, textTransform: "none" },
};

const shape = { borderRadius: 8 };

const palettes = {
    forest: {
        mode: "dark",
        primary: {
            main: "#85BC6E",
            light: "#96C882",
            dark: "#5F8A4E",
            contrastText: "#0A0A0A",
        },
        secondary: {
            main: "#5F8A4E",
            light: "#85BC6E",
            dark: "#3F5C34",
            contrastText: "#F4F4F4",
        },
        error: { main: "#FF6B6B" },
        warning: { main: "#F9CC6C" },
        info: { main: "#7EB8DA" },
        success: { main: "#85BC6E" },
        background: { default: "#050505", paper: "#161616" },
        text: { primary: "#F4F4F4", secondary: "#A0A0A0" },
        divider: "rgba(133, 188, 110, 0.4)",
    },
    ocean: {
        mode: "dark",
        primary: { main: "#58A6FF", light: "#79B8FF", dark: "#388BFD", contrastText: "#0D1117" },
        secondary: { main: "#388BFD", light: "#58A6FF", dark: "#1F6FEB", contrastText: "#F0F6FC" },
        error: { main: "#F85149" },
        warning: { main: "#D29922" },
        info: { main: "#58A6FF" },
        success: { main: "#3FB950" },
        background: { default: "#0D1117", paper: "#161B22" },
        text: { primary: "#F0F6FC", secondary: "#8B949E" },
        divider: "rgba(240, 246, 252, 0.12)",
    },
    slate: {
        mode: "dark",
        primary: { main: "#94A3B8", light: "#CBD5E1", dark: "#64748B", contrastText: "#0F172A" },
        secondary: { main: "#64748B", light: "#94A3B8", dark: "#475569", contrastText: "#F8FAFC" },
        error: { main: "#F87171" },
        warning: { main: "#FBBF24" },
        info: { main: "#38BDF8" },
        success: { main: "#4ADE80" },
        background: { default: "#0F172A", paper: "#1E293B" },
        text: { primary: "#F8FAFC", secondary: "#94A3B8" },
        divider: "rgba(248, 250, 252, 0.1)",
    },
    light: {
        mode: "light",
        primary: { main: "#5F8A4E", light: "#85BC6E", dark: "#3F5C34", contrastText: "#FFFFFF" },
        secondary: { main: "#85BC6E", light: "#96C882", dark: "#5F8A4E", contrastText: "#0A0A0A" },
        error: { main: "#C62828" },
        warning: { main: "#F57C00" },
        info: { main: "#1565C0" },
        success: { main: "#5F8A4E" },
        background: { default: "#F4F6F8", paper: "#FFFFFF" },
        text: { primary: "#1A1D21", secondary: "#5C6570" },
        divider: "rgba(0, 0, 0, 0.09)",
    },
};

export const THEME_STORAGE_KEY = "company-manage-theme-id";

export const THEME_CHOICES = [
    { id: "forest", name: "PBR Core", description: "Site-matched black with #85BC6E lime accents" },
    { id: "ocean", name: "Ocean", description: "Cool blues, high-contrast dark" },
    { id: "slate", name: "Slate", description: "Neutral charcoal and silver" },
    { id: "light", name: "Daylight", description: "Light surfaces, green brand" },
];

export function createAppTheme(themeId = "forest") {
    const base = palettes[themeId] ? { ...palettes[themeId] } : { ...palettes.forest };
    const isDark = base.mode === "dark";
    // Explicit action colors — ListItemButton (and others) read these; avoid washed-out / wrong CSS var resolution.
    const palette = {
        ...base,
        action: {
            ...(base.action || {}),
            hover: isDark ? alpha("#ffffff", 0.07) : alpha("#000000", 0.06),
            selected: isDark ? alpha("#ffffff", 0.12) : alpha("#000000", 0.06),
        },
    };

    return createTheme({
        palette,
        typography,
        shape,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        scrollbarColor:
                            palette.mode === "dark"
                                ? `${alpha("#fff", 0.2)} ${alpha("#fff", 0.05)}`
                                : undefined,
                    },
                },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundImage: "none",
                        border: `1px solid ${theme.palette.divider}`,
                    }),
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: theme.shape.borderRadius + 2,
                    }),
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 600, borderRadius: 8 },
                    filled: ({ theme }) => ({
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }),
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                    },
                    containedPrimary: ({ theme }) => ({
                        color: theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }),
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0, color: "default" },
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundColor: theme.palette.mode === "dark" ? alpha("#050505", 0.96) : alpha(theme.palette.background.paper, 0.92),
                        backdropFilter: "blur(12px)",
                        color: theme.palette.text.primary,
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.4) : theme.palette.divider}`,
                    }),
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        backgroundImage: "none",
                        border: `1px solid ${theme.palette.divider}`,
                    }),
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderColor: theme.palette.divider,
                    }),
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: ({ theme }) => ({
                        backgroundColor: theme.palette.primary.main,
                        height: 3,
                        borderRadius: 3,
                    }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: "none",
                        fontWeight: 600,
                        "&.Mui-selected": {
                            color: theme.palette.primary.main,
                        },
                    }),
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: 10,
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(
                                theme.palette.mode === "dark" ? "#fff" : "#000",
                                theme.palette.mode === "dark" ? 0.15 : 0.15
                            ),
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                        },
                        "& input:-webkit-autofill": {
                            WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
                            WebkitTextFillColor: theme.palette.text.primary,
                            caretColor: theme.palette.text.primary,
                            borderRadius: "inherit",
                        },
                    }),
                },
            },
            // MUI v6 bakes :hover / .selected into ListItemButton's styled root; plain sx often loses.
            // `&&` doubles class specificity so these beat the defaults. ListItemButton is only used in Sidebar.
            MuiListItemButton: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: ({ theme }) => {
                        const dark = theme.palette.mode === "dark";
                        const hoverBg = dark
                            ? alpha(theme.palette.common.white, 0.08)
                            : alpha(theme.palette.common.black, 0.06);
                        const selectedBg = dark
                            ? alpha(theme.palette.common.white, 0.1)
                            : alpha(theme.palette.primary.main, 0.12);
                        const selectedHoverBg = dark
                            ? alpha(theme.palette.common.white, 0.14)
                            : alpha(theme.palette.primary.main, 0.18);
                        const fg = theme.palette.text.primary;
                        const muted = theme.palette.text.secondary;
                        const bar = theme.palette.primary.main;

                        return {
                            color: muted,
                            "& .MuiListItemText-primary": {
                                color: "inherit",
                                fontWeight: 500,
                                fontSize: "0.9375rem",
                            },
                            "&&:hover": {
                                backgroundColor: hoverBg,
                                color: fg,
                            },
                            "&&:hover .MuiListItemText-primary": {
                                color: fg,
                            },
                            "&&.Mui-selected": {
                                backgroundColor: selectedBg,
                                color: fg,
                                boxShadow: `inset 3px 0 0 ${bar}`,
                            },
                            "&&.Mui-selected .MuiListItemText-primary": {
                                color: fg,
                                fontWeight: 600,
                            },
                            "&&.Mui-selected:hover": {
                                backgroundColor: selectedHoverBg,
                                color: fg,
                            },
                            "&&.Mui-selected:hover .MuiListItemText-primary": {
                                color: fg,
                            },
                        };
                    },
                },
            },
        },
    });
}
